// Named audio event hooks. Every player-facing mechanic fires one of these.
// The client keeps an exhaustive placeholder table (TONES) keyed by AudioHook, so adding a hook
// without a placeholder fails typecheck; assets/audio/SFX_MANIFEST.md documents the intended sound
// and a server test asserts every hook has a manifest row.

export type AudioBus = "ambient" | "alert" | "ui" | "sfx";

export interface AudioHookMeta {
  bus: AudioBus;
  /** Attenuated/panned by deck-map distance from the listener when true. */
  spatial: boolean;
  /** Continuous sound managed by loop()/stop() rather than a one-shot. */
  loop?: boolean;
}

export const AUDIO_HOOKS = {
  "ui.boot":              { bus: "ui",      spatial: false },
  "ui.denied":            { bus: "ui",      spatial: false },
  "crew.join":            { bus: "ui",      spatial: false },
  "crew.leave":           { bus: "ui",      spatial: false },
  "crew.link.lost":       { bus: "alert",   spatial: false },
  "crew.link.restored":   { bus: "ui",      spatial: false },
  "mission.start":        { bus: "alert",   spatial: false },
  "mission.won":          { bus: "alert",   spatial: false },
  "mission.lost":         { bus: "alert",   spatial: false },
  "move.depart":          { bus: "sfx",     spatial: true },
  "move.arrive":          { bus: "sfx",     spatial: true },
  "move.footsteps.loop":  { bus: "sfx",     spatial: true,  loop: true },
  "reactor.hum.loop":     { bus: "ambient", spatial: false, loop: true },
  "reactor.low":          { bus: "alert",   spatial: false },
  "reactor.critical":     { bus: "alert",   spatial: false },
  "reactor.scram":        { bus: "alert",   spatial: false },
  "breaker.trip":         { bus: "sfx",     spatial: true },
  "breaker.flip":         { bus: "sfx",     spatial: true },
  "breaker.wrong":        { bus: "sfx",     spatial: true },
  "breaker.reset":        { bus: "sfx",     spatial: true },
  "breaker.unlock":       { bus: "sfx",     spatial: true },
  "hold.press":           { bus: "sfx",     spatial: true },
  "hold.release":         { bus: "sfx",     spatial: true },
  "hold.waiting":         { bus: "alert",   spatial: false },
  "hold.engaged":         { bus: "sfx",     spatial: false },
  "hold.engaged.loop":    { bus: "sfx",     spatial: false, loop: true },
  "hold.success":         { bus: "alert",   spatial: false },
  "hold.fail":            { bus: "alert",   spatial: false },
  "hold.ready":           { bus: "ui",      spatial: false },
  "intercom.open":        { bus: "ui",      spatial: false },
  "intercom.mute":        { bus: "ui",      spatial: false },
  "intercom.unmute":      { bus: "ui",      spatial: false },
  "intercom.peer.link":   { bus: "ui",      spatial: false },
  "intercom.peer.lost":   { bus: "alert",   spatial: false },
  "power.set":            { bus: "sfx",     spatial: true },
  "power.brownout":       { bus: "alert",   spatial: false },
  "o2.low":               { bus: "alert",   spatial: false },
  "o2.critical":          { bus: "alert",   spatial: false },
  "o2.restored":          { bus: "ui",      spatial: false },
  "coolant.leak":         { bus: "alert",   spatial: true },
  "coolant.sealed":       { bus: "alert",   spatial: false },
  "coolant.backflow":     { bus: "alert",   spatial: false },
  "relay.damaged":        { bus: "alert",   spatial: false },
  "relay.taken":          { bus: "sfx",     spatial: true },
  "relay.socketed":       { bus: "sfx",     spatial: true },
  "director.calm":        { bus: "ambient", spatial: false },
  "ship.idle.loop":       { bus: "ambient", spatial: false, loop: true },
  "drill.start":          { bus: "alert",   spatial: false },
  "drill.complete":       { bus: "ui",      spatial: false },
  // Station fixtures: the switches, buttons, dials and drawers you can reach from your seat.
  // Most are unbound scenery today; the ones a task claims fire the task's own hook as well.
  "fixture.switch":       { bus: "sfx",     spatial: false },
  "fixture.button":       { bus: "sfx",     spatial: false },
  "fixture.lever":        { bus: "sfx",     spatial: false },
  "fixture.dial":         { bus: "sfx",     spatial: false },
  "fixture.drawer":       { bus: "sfx",     spatial: false },
  "fixture.stuck":        { bus: "sfx",     spatial: false },
} as const satisfies Record<string, AudioHookMeta>;

export type AudioHook = keyof typeof AUDIO_HOOKS;
export const AUDIO_HOOK_LIST = Object.keys(AUDIO_HOOKS) as AudioHook[];
export const isAudioHook = (s: string): s is AudioHook => s in AUDIO_HOOKS;
